import { Type } from "@angular/core";
import { ManualsDownloadedDetailsComponent } from "./charts/manuals-downloaded/details/manuals-downloaded-details.component";
import { ManualsDownloadedLeastDetailsComponent } from "./charts/manuals-downloaded-least/details/manuals-downloaded-least-details.component";
import { ManualsDownloadedMostDetailsComponent } from "./charts/manuals-downloaded-most/details/manuals-downloaded-most-details.component";
import { ManualsDownloadedNeverDetailsComponent } from "./charts/manuals-downloaded-never/details/manuals-downloaded-never-details.component";
import { ManualsOpenedDetailsComponent } from "./charts/manuals-opened/details/manuals-opened-details.component";
import { ManualsOpenedCurrentDetailsComponent } from "./charts/manuals-opened-current/details/manuals-opened-current-details.component";
import { ManualsCreatedDetailsComponent } from "./charts/manuals-created/details/manuals-created-details.component";
import { ManualsUpdatedDetailsComponent } from "./charts/manuals-updated/details/manuals-updated-details.component";
import { ManualsPrintedDetailsComponent } from "./charts/manuals-printed/details/manuals-printed-details.component";
import { AnnotationsSentDetailsComponent } from "./charts/annotations/details/annotations-sent-details.component";
import { PagesViewedDetailsComponent } from "./charts/pages-viewed/details/pages-viewed-details.component";
import { ManualsLanguageDetailsComponent } from "./charts/manuals-language/details/manuals-language-details.component";
import { SearchTermDetailsComponent } from "./charts/top-search-terms/details/search-term-details.component";
import { UsersCurrentDetailsComponent } from "./charts/users-current/details/users-current-details.component";
import { UsersCreatedDetailsComponent } from "./charts/users-created/details/users-created-details.component";
import { UsersCreatedTotalDetailsComponent } from "./charts/users-created-total/details/users-created-details-total.component";
import { UsersLockedDetailsComponent } from "./charts/users-locked/details/users-locked-details.component";
import { UsersLockedTotalDetailsComponent } from "./charts/users-locked-total/details/users-locked-details-total.component";
import { UsersRemovedDetailsComponent } from "./charts/users-removed/details/users-removed-details.component";
import { UsersRemovedTotalDetailsComponent } from "./charts/users-removed-total/details/users-removed-details-total.component";
import { AppVersionDetailsComponent } from "./charts/app-version/details/app-version-details.component";
import { OsTypeDetailsComponent } from "./charts/os-type-counts/details/os-type-details.component";
import { OsVersionsDetailsComponent } from "./charts/os-version-counts/details/os-versions-details.component";
import { UsersTotalDetailsComponent } from "./charts/users-total/details/users-total-details.component";
import { AppLanguageDetailsComponent } from "./charts/app-language/details/app-language-details.component";
import { UsersSessionsActiveDetailsComponent } from "./charts/users-sessions-active/details/users-sessions-active-details.component";
import { UsersSessionsDetailsComponent } from "./charts/users-sessions/details/users-sessions-details.component";
import { AppUsageDetailsComponent } from "./charts/app-usage/details/app-usage-details.component";
import { GlobalSearchTermDetailsComponent } from "./charts/global-search-terms/details/global-search-term-details.component";
import { GlobalSearchTermDocumentsDetailsComponent } from "./charts/global-search-terms-documents/details/global-search-term-documents-details.component";
import { CombinedSearchTermsDetailsComponent } from './charts/combined-search-terms/details/combined-search-terms-details.component';

export enum AnalyticsType {
  User = "User",
  Manuals = "Manuals",
  App = "App",
}

/**
 * Defines the chart details for analytics. In a single object so it can be used to build details components dynamically (list and individual components).
 * properties:
 *  title: The title of the chart
 *  uniqueId: A unique identifier for the chart used to reference saved configs, if not used the title will be used
 *  type: The type of analytics data the chart is for
 *  description: A description of the chart
 *  sortOrder: The order in which the chart should be displayed, they are sorted by this value, then alphabetically by title
 *  detailsComponent: The component to display the chart details, ref used to dynamically load the component
 *  chartTypeOptions: The chart types that can be displayed for the chart
 *  labelOptions: The labels that can be displayed for the chart, IMPORTANT: these are a direct map to the data
 *  customLabels: Custom labels that initiate custom handling for unique data
 *  excludeColumns: Columns to exclude from the data, when displaying raw data, examples are "Document.CoverImage" because it is byte[]
 *  defaultLabels: The default labels to display when the chart is first loaded, and if no labels are selected in options
 */
export interface ChartDefinition {
  title: string;
  uniqueId?: string;
  type: AnalyticsType;
  shortDescription?: string;
  fullDescription?: string;
  sortOrder: number;
  detailsComponent: Type<any>;
  chartTypeOptions?: DisplayChartType[];
  labelOptions?: string[];
  customLabels?: { [key: string]: string[] };
  excludeColumns?: string[];
  defaultLabels?: string[];
  hideTotalColumn?: boolean; // If true, the total column will not be displayed in the chart
}

export enum DisplayChartType {
  //Mixed = "Mixed",
  Area = "area", //Area chart is a line or radar chart where the area under the line is filled with a color
  Bar = "bar",
  Bubble = "bubble", //three dimensional chart
  Doughnut = "doughnut",
  Pie = "pie",
  Line = "line",
  PolarArea = "polarArea",
  Radar = "radar",
  Scatter = "scatter",
  List = "list",
  Grid = "grid",
}

const barChartOptions = [
  DisplayChartType.Pie,
  DisplayChartType.Doughnut,
  DisplayChartType.Bar,
  DisplayChartType.List,
];

const listOnlyOption = [DisplayChartType.List];

const gridOnlyOption = [DisplayChartType.Grid];

const lineChartOptions = [DisplayChartType.Line, DisplayChartType.List];

const gridOptions = [DisplayChartType.Grid];

const documentObjectMap = [
  "Document.FileName",
  "Document.ProductName",
  "Document.Version",
  "Document.LastUpdated",
  "Document.VersionCreatedDate",
];

const documentFlatMap = [
  "FileName",
  "ProductName",
  "Version",
  "LastUpdated",
  "VersionCreatedDate",
];

const UserActionFlatMap = [
  "ModifiedBy",
  "Date",
  "User.Id",
  "User.FirstName",
  "User.LastName",
  "User.UserName",
  "User.IsDeleted",
];

const languageObjectMap = ["Language.Name"];

const excludeDocumentColumns = ["Document.CoverImage"];

const fmiProductObjectMap = [
  "FMIProduct.ProductName",
  "FMIProduct.ProductDescription",
  "FMIProduct.PubType",
  "FMIProduct.Language",
  "FMIProduct.Model",
  "FMIProduct.Division",
  "FMIProduct.ProManualTitle",
  "FMIProduct.TitaniaUrl",
];

const applicationLanguageObjectMap = [
  "Language.Name",
  "Language.Description",
  "Language.PM_Code",
  "Language.PM_Description",
  "Language.Active",
];

const userAnalyticsTotalDataPointMap = ["PermissionType"];

const dataPointForUserAnalyticsMap = ["PermissionType"];

const dataPointForTotalUserAnalyticsMap = ["RangeStart", "RangeEnd"];

const dataPointForRemoveActionsMap = [
  "PermissionType",
  "User.FirstName",
  "User.LastName",
  "User.UserName",
  "User.IsDeleted",
  "RemovedBy",
  "RemoveDate",
];

const dataPointForLockActionsMap = [
  "PermissionType",
  "User.FirstName",
  "User.LastName",
  "User.UserName",
  "User.IsDeleted",
  "LockedBy",
  "LockDate",
];

const dataPointForCreatedUsersMap = [
  "User.FirstName",
  "User.LastName",
  "User.UserName",
  "User.IsDeleted",
  "User.Registered",
  "User.CreatedBy",
];

const userAnalyticsDataPointMap = ["PermissionType"];

const downloadDataDetailsMap = [...documentObjectMap, ...fmiProductObjectMap];

const appVersionObjectMap = [
  "Version.AppType",
  "Version.VersionNumber",
  "Version.Released",
  "Version.RequiredUpdate",
  "Version.CurrentVersion",
  "Version.MinVersion",
];

const globalSearchTermDocumentDataMap = [
  "SearchTerm",
  "Document.FileName",
  "Document.ProductName",
  "Document.Version",
  "FMIProduct.ProductName",
  "FMIProduct.ProManualTitle",
];

const combinedSearchTermDataMap = [
    "SearchTerm",
    "Total",
    "DistinctUserCount"
];


/**
 * Defines the ChartDefinitions and associated components for analytics chart details.
 */
export const ChartDefinitions: { [key: string]: ChartDefinition } = {
  AppUsage: {
    title: "App Usage",
    uniqueId: "AppUsage",
    type: AnalyticsType.User,
    shortDescription:
      "Track user activity by type of user, session duration, manual(s) viewed, search terms input, and pages viewed.",
    fullDescription: `
  Tracks app usage by type of user, session duration, manual(s) viewed, search terms input, and pages viewed.<br>
  <b>Time Period:</b> Search for user activity within the selected time period. (Now - Time Period)<br>
  <b>Permission Groups:</b> Displays data broken into types based off of Type of User. Shows Permission Type and the Total Number Of Users in that group<br>
  <b>Users:</b> Displays the name of the user who belongs to the selected Permission Group<br>
  <b>Sessions:</b> Displays a list of all of the sessions (a period of time spend using ProManual) has during the given time period as well as a <i>View Details</i> button.<br>
  <b>View Details Popup:</b> Displays further information about a specific session. Documents Names, what pages were viewed (and how many times), what terms were searched (and how many times), and when the documents were opened are all included.
`,
    sortOrder: 1,
    detailsComponent: AppUsageDetailsComponent,
    chartTypeOptions: gridOnlyOption,
    labelOptions: ["Document.FileName"],
    defaultLabels: ["Document.FileName"],
  },
  ManualsDownloaded: {
    title: "Downloaded Manuals",
    uniqueId: "ManualsDownloaded",
    type: AnalyticsType.Manuals,
    shortDescription: "Manuals downloaded by title within a period of time.",
    fullDescription: `
  Manuals downloaded by title within a period of time.<br>
  <b>Total:</b> Number of times an individual manual was downloaded. Minimum downloads allows filtering of data to records that have been downloaded at least X amount of times.<br>
  <b>FMI Product:</b> The manual and settings set via the Document Management list and modal in Document Management.<br>
  <b>Document:</b> Individual versions of manuals uploaded for the FMI Product.
`,

    sortOrder: 1,
    detailsComponent: ManualsDownloadedDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: downloadDataDetailsMap,
    excludeColumns: excludeDocumentColumns,
    defaultLabels: ["Document.ProductName", "Document.FileName"],
  },
  ManualsDownloadedLeast: {
    title: "Least Downloaded Manuals",
    uniqueId: "ManualsDownloadedLeast",
    type: AnalyticsType.Manuals,
    shortDescription:
      "Bottom X number of downloaded manuals within a period of time.",
    fullDescription: `
  Bottom X number of downloaded manuals within a period of time.<br>
  <b>Product Name:</b> Displayed name of the manual in the app.<br>
  <b>File Name:</b> Actual file name and extension stored.<br>
  <b>Total:</b> Number of times the manual has been downloaded.
`,
    sortOrder: 2,
    detailsComponent: ManualsDownloadedLeastDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: downloadDataDetailsMap,
    excludeColumns: excludeDocumentColumns,
    defaultLabels: ["Document.ProductName", "Document.FileName"],
  },
  ManualsDownloadedMost: {
    title: "Most Downloaded Manuals",
    uniqueId: "ManualsDownloadedMost",
    type: AnalyticsType.Manuals,
    shortDescription:
      "Top X number of downloaded manuals within a period of time.",
    fullDescription: `
  Top X number of downloaded manuals within a period of time.<br>
  <b>Product Name:</b> Displayed name of the manual in the app.<br>
  <b>File Name:</b> Actual file name and extension stored.<br>
  <b>Total:</b> Number of times the manual has been downloaded.
`,
    sortOrder: 3,
    detailsComponent: ManualsDownloadedMostDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: downloadDataDetailsMap,
    excludeColumns: excludeDocumentColumns,
    defaultLabels: ["Document.ProductName", "Document.FileName"],
  },
  ManualsDownloadedNever: {
    title: "Never Downloaded Manuals",
    uniqueId: "ManualsDownloadedNever",
    type: AnalyticsType.Manuals,
    shortDescription: "List of Manuals that have never been downloaded.",
    fullDescription: `
  List of Manuals that have never been downloaded within a period of time.<br>
  <b>Product Name:</b> Displayed name of the manual in the app.<br>
  <b>File Name:</b> Actual file name and extension stored.<br>
  <b>Total:</b> Number of times the manual has been downloaded.
`,
    sortOrder: 4,
    detailsComponent: ManualsDownloadedNeverDetailsComponent,
    chartTypeOptions: [DisplayChartType.List],
    labelOptions: downloadDataDetailsMap,
    excludeColumns: [...excludeDocumentColumns, "Total"],
    defaultLabels: ["Document.ProductName", "Document.FileName"],
    hideTotalColumn: true, // Hides the total column in the list view
  },
  ManualsOpened: {
    title: "Opened Manuals",
    uniqueId: "ManualsOpened",
    type: AnalyticsType.Manuals,
    shortDescription: "Manuals opened within a period of time.",
    fullDescription: `
  Manuals that have been opened within the selected Time Period.<br>
  <b>Filters:</b> Use ‘Min Opened’ to filter the data for manuals opened a minimum number of times. ‘Top’ will only return the top x manuals based on number of times opened.<br>
  <b>Dates Opened:</b> Select as a ‘Column’ in list view to make individual rows expandable with a sub-table of the dates that particular manual was opened. No data will be available for URL-based manuals.<br>
  <b>Pages Viewed:</b> Select as a ‘Column’ in list view to make individual rows expandable with a sub-table of pages viewed for that particular manual. The Filter Data field ‘Pages Viewed’ controls the columns available in those sub-tables. No data will be available for URL-based manuals.<br>
  <b>Search Term Details:</b> Select as a ‘Column’ in list view to make individual rows expandable with a sub-table of search terms used for that particular manual. The ‘Search Term Details’ filter field controls the columns available in those sub-tables. No data will be available for URL-based manuals.<br>
  <b>Total:</b> Number of times an individual manual was opened in the given time period.<br>
  <b>FMI Product:</b> The manual and settings set via the Document Management list and modal in Document Management.<br>
  <b>Document:</b> Individual versions of manuals uploaded for the FMI Product.
`,

    sortOrder: 5,
    detailsComponent: ManualsOpenedDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: downloadDataDetailsMap,
    excludeColumns: excludeDocumentColumns,
    defaultLabels: ["Document.ProductName", "Document.FileName"],
    customLabels: {
      "Dates Opened": ["Date", "Time", "First Name", "Last Name", "User Name"],
      "Pages Viewed": ["Page", "Total"],
      "Search Terms Used": ["Search Term", "Total"],
    },
  },
  ManualsOpenedCurrent: {
    title: "Currently Open Manuals",
    uniqueId: "ManualsOpenedCurrent",
    type: AnalyticsType.Manuals,
    shortDescription:
      "Displays which manuals are currently open within a set time.",
    fullDescription: `
  Displays which manuals are currently open within a set time.<br>
  <b>Product Name:</b> Displayed name of the manual in the app.<br>
  <b>File Name:</b> Actual file name and extension stored.<br>
  <b>Version:</b> Which version of the document is opened.<br>
  <b>Total:</b> Number of users that have it open currently.
`,
    sortOrder: 6,
    detailsComponent: ManualsOpenedCurrentDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: downloadDataDetailsMap,
    excludeColumns: excludeDocumentColumns,
    defaultLabels: [
      "Document.ProductName",
      "Document.FileName",
      "Document.Version",
    ],
    customLabels: {
      "Dates Opened": ["Date", "Time", "First Name", "Last Name", "User Name"],
      "Pages Viewed": ["Page", "Total"],
      "Search Terms Used": ["Search Term", "Total"],
    },
  },
  ManualsCreated: {
    title: "Manuals Created",
    uniqueId: "ManualsCreated",
    type: AnalyticsType.Manuals,
    shortDescription:
      "Shows new manuals created within the selected time period.",
    fullDescription: `
  Shows new manuals created within the selected time period.<br>
  <b>File Name:</b> Actual file name and extension stored.<br>
  <b>Updated:</b> When this document was created.<br>
  <b>CreatedByUser:</b> Email of the user who created the document.
`,
    sortOrder: 7,
    detailsComponent: ManualsCreatedDetailsComponent,
    chartTypeOptions: listOnlyOption,
    labelOptions: [...downloadDataDetailsMap, "CreatedByUser"],
    excludeColumns: [...excludeDocumentColumns, "Total"],
    defaultLabels: [
      "Document.FileName",
      "Document.LastUpdated",
      "CreatedByUser",
    ],
    hideTotalColumn: true, // Hides the total column in the list view
  },
  ManualsUpdated: {
    title: "Manuals Updated",
    uniqueId: "ManualsUpdated",
    type: AnalyticsType.Manuals,
    shortDescription:
      "Displays manuals updated within the selected time period.",
    fullDescription: `
  Displays manuals updated within the selected time period.<br>
  <b>File Name:</b> Actual file name and extension stored.<br>
  <b>Updated:</b> When this document was updated.<br>
  <b>UpdatedByUser:</b> Email of the user who created the document.<br>
  <b>Total:</b> Number of times the document has been updated.
`,
    sortOrder: 8,
    detailsComponent: ManualsUpdatedDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: [...downloadDataDetailsMap, "UpdatedByUser"],
    excludeColumns: excludeDocumentColumns,
    defaultLabels: [
      "Document.FileName",
      "Document.LastUpdated",
      "UpdatedByUser",
    ],
  },
  ManualsPrinted: {
    title: "Manuals Printed",
    uniqueId: "ManualsPrinted",
    type: AnalyticsType.Manuals,
    shortDescription:
      "Displays which manuals have been printed within the selected time period.",
    fullDescription: `
  Displays which manuals have been printed within the selected time period.<br>
  <b>Product Name:</b> Displayed name of the manual in the app.<br>
  <b>File Name:</b> Actual file name and extension stored.<br>
  <b>Version:</b> Which version of the document was printed.<br>
  <b>Total:</b> Number of times the document has been printed.
`,
    sortOrder: 10,
    detailsComponent: ManualsPrintedDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: [...documentObjectMap],
    customLabels: {
      "User Data": ["User.FullName", "User.Email", "DatePrinted", "Total"],
    },
    excludeColumns: excludeDocumentColumns,
    defaultLabels: [
      "Document.ProductName",
      "Document.FileName",
      "Document.Version",
    ],
  },
  AnnotationsSent: {
    title: "Annotations Sent",
    uniqueId: "AnnotationsSent",
    type: AnalyticsType.Manuals,
    shortDescription:
      "Shows manuals from which annotations were sent within the selected time period.",
    fullDescription: `
  Shows manuals from which annotations were sent within the selected time period.<br>
  Data includes tracking of annotations, who sent, from what manual they came from, what version of the manual, etc.
`,
    sortOrder: 11,
    detailsComponent: AnnotationsSentDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: downloadDataDetailsMap,
    customLabels: {
      "User Data": ["User.FullName", "User.Email", "Date Sent", "Total"],
      Annotations: [
        "Annotation.User",
        "Annotation.Email",
        "Annotation.Note",
        "Annotation.Submitted",
      ],
    },
    excludeColumns: excludeDocumentColumns,
    defaultLabels: [
      "Document.ProductName",
      "Document.FileName",
      "Document.Version",
    ],
  },
  PagesViewed: {
    title: "Pages Viewed",
    uniqueId: "PagesViewed",
    type: AnalyticsType.Manuals,
    shortDescription:
      "Displays pages viewed with manuals within the selected time period.",
    fullDescription: `
  Displays pages viewed with manuals within the selected time period.<br>
  <b>File Name:</b> Actual file name and extension stored.<br>
  <b>Version:</b> Which version of the document was opened.<br>
  <b>PageNumberViewed:</b> Which page within the manual was viewed. Can be adjusted by how long the page was viewed<br>
  <b>Total:</b> Number of times all users viewed that page.<br>
`,
    sortOrder: 12,
    detailsComponent: PagesViewedDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: [...documentObjectMap, "PageNumberViewed"],
    excludeColumns: excludeDocumentColumns,
    defaultLabels: [
      "Document.FileName",
      "Document.Version",
      "PageNumberViewed",
    ],
  },
  ManualsLanguage: {
    title: "Manuals Language",
    uniqueId: "ManualsLanguage",
    type: AnalyticsType.App,
    shortDescription: "Manual Language set by users in app.",
    fullDescription: `
  Manuals language is set by users within the mobile apps via settings. It is the default manual language.<br>
  <b>Total:</b> A current snapshot of users’ settings based on their most recent log in.<br>
  <i><b>Note:</b> Totals may not represent complete user counts and are limited to available data.</i><br>
  <b>Name:</b> The short and full name for the selected language.
`,

    sortOrder: 2,
    detailsComponent: ManualsLanguageDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: [...languageObjectMap],
    excludeColumns: [],
    defaultLabels: ["Language.Name"],
  },
  SearchTerms: {
    title: "Document Search Terms",
    uniqueId: "SearchTerms",
    type: AnalyticsType.Manuals,
    shortDescription:
      "Displays X Number of the top search terms users searched in a manual.",
    fullDescription: `
  Displays X Number of the top search terms users searched in a manual.<br>
  <b>Search Term:</b> Specific term/phrase that was searched.<br>
  <b>Total:</b> Number of times all users searched that term.<br>
`,
    sortOrder: 14,
    detailsComponent: SearchTermDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: ["SearchTerm"],
    excludeColumns: excludeDocumentColumns,
    defaultLabels: ["SearchTerm"],
    customLabels: {
      "Document Count": ["count"],
      Documents: [...documentFlatMap],
    },
  },
  GlobalSearchTerms: {
    title: "Global Search Terms",
    uniqueId: "GlobalSearchTerms",
    type: AnalyticsType.Manuals,
    shortDescription:
      "Displays the most popular search terms across all manuals.",
    fullDescription: `
  Shows the top search terms entered by users across the entire application, not limited to a single manual.<br>
  <b>Search Term:</b> The term or phrase searched.<br>
  <b>Total:</b> Number of times the term was searched.<br>
  <b>Documents:</b> Optionally, view which documents were returned for each search term.
`,
    sortOrder: 15,
    detailsComponent: GlobalSearchTermDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: ["Term"],
    excludeColumns: excludeDocumentColumns,
    defaultLabels: ["Term"],
    customLabels: {},
  },
  GlobalSearchTermDocuments: {
    title: "Global Search Term Documents",
    uniqueId: "GlobalSearchTermDocuments",
    type: AnalyticsType.Manuals,
    shortDescription:
      "Displays documents returned for the most popular global search terms.",
    fullDescription: `
  Shows which documents are most frequently returned for the top search terms entered by users across the entire application.<br>
  <b>Search Term:</b> The term or phrase searched.<br>
  <b>Document:</b> The document returned for the search term.<br>
  <b>Total:</b> Number of times the document was returned for the search term.<br>
`,
    sortOrder: 16,
    detailsComponent: GlobalSearchTermDocumentsDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: globalSearchTermDocumentDataMap,
    excludeColumns: [],
    defaultLabels: ["SearchTerm", "Document.FileName", "Document.ProductName"],
    customLabels: {},
    },
    CombinedSearchTerms: {
        title: "Combined Search Terms",
        uniqueId: "CombinedSearchTerms",
        type: AnalyticsType.Manuals,
        shortDescription:
            "Displays combined search terms from both global and document-specific searches.",
        fullDescription: `
  Shows all search terms aggregated from both global searches (across the entire application) and document-specific searches.<br>
  <b>Search Term:</b> The term or phrase searched across all sources.<br>
  <b>Distinct Users:</b> Number of unique users who searched this term.<br>
  <i><b>Note:</b> Total searches are automatically displayed and represent the combined count from both global and document-specific searches.</i>
`,
        sortOrder: 17,
        detailsComponent: CombinedSearchTermsDetailsComponent,
        chartTypeOptions: barChartOptions,
        labelOptions: ["SearchTerm", "DistinctUserCount"],
        excludeColumns: [...excludeDocumentColumns, "Total"],
        defaultLabels: ["SearchTerm", "DistinctUserCount"],
        customLabels: {},
    },
  UsersCurrent: {
    title: "Current Users",
    uniqueId: "UsersCurrent",
    type: AnalyticsType.User,
    shortDescription: "Total current users by permission type.",
    fullDescription: `
  Current Users include all active users, excluding deleted, locked, and archived users.<br>
  <b>Total:</b> A current snapshot of active users.<br>
  <b>PermissionType:</b> The type of user or user level.
`,

    sortOrder: 1,
    detailsComponent: UsersCurrentDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: [...userAnalyticsTotalDataPointMap],
    excludeColumns: ["TotalUsers"],
    defaultLabels: [...userAnalyticsTotalDataPointMap],
  },
  UsersCreated: {
    title: "Created Users",
    uniqueId: "UsersCreated",
    type: AnalyticsType.User,
    shortDescription: "Users created within a period of time.",
    fullDescription: `
  Users created by permission type within a period of time.<br>
  <b>User.FirstName:</b> The first name of the user.<br>
  <b>User.LastName:</b> The last name of the user.<br>
  <b>User.UserName:</b> The user's username.<br>
  <b>User.IsDeleted:</b> Whether or not the user has been deleted.<br>
  <b>User.RegistrationDate:</b> When the user's account was first created.<br>
  <b>User.CreatedBy:</b> Who created the user account.
`,

    sortOrder: 2,
    detailsComponent: UsersCreatedDetailsComponent,
    chartTypeOptions: listOnlyOption,
    labelOptions: [...dataPointForCreatedUsersMap],
    excludeColumns: [
      "TypeOfUser",
      "PermissionType",
      "PermissionId",
      "TotalUsers",
    ],
    defaultLabels: [...dataPointForCreatedUsersMap],
  },
  UsersCreatedTotal: {
    title: "Created Users Total",
    uniqueId: "UsersCreatedTotal",
    type: AnalyticsType.User,
    shortDescription:
      "Users created by permission type within a period of time.",
    fullDescription: `
  Users created by permission type within a period of time.<br>
  <b>Total:</b> Number of users created during the time period.<br>
  <b>PermissionType:</b> The type of user or user level.
`,
    sortOrder: 3,
    detailsComponent: UsersCreatedTotalDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: [...userAnalyticsTotalDataPointMap],
    excludeColumns: ["TotalUsers"],
    defaultLabels: [...userAnalyticsTotalDataPointMap],
  },
  UsersLocked: {
    title: "Locked Users",
    uniqueId: "UsersLocked",
    type: AnalyticsType.User,
    shortDescription: "Users locked within a period of time.",
    fullDescription: `
  Users locked by permission type within a period of time.<br>
  <b>PermissionType:</b> The type of user or user level.<br>
  <b>User.FirstName:</b> The first name of the user.<br>
  <b>User.LastName:</b> The last name of the user.<br>
  <b>User.UserName:</b> The user's username.<br>
  <b>User.IsDeleted:</b> Whether or not the user has been deleted.<br>
  <b>LockedBy:</b> Who locked the user account.<br>
  <b>LockedDate:</b> When the user account was locked.
`,
    sortOrder: 4,
    detailsComponent: UsersLockedDetailsComponent,
    chartTypeOptions: listOnlyOption,
    labelOptions: [...dataPointForLockActionsMap],
    excludeColumns: ["TotalUsers"],
    defaultLabels: [...dataPointForLockActionsMap],
  },
  UsersLockedTotal: {
    title: "Locked Users Total",
    uniqueId: "UsersLockedTotal",
    type: AnalyticsType.User,
    shortDescription:
      "Users locked by permission type within a period of time.",
    fullDescription: `
Users locked by permission type within a period of time.<br>
<b>Total:</b> Number of users locked during the time period.<br>
<b>PermissionType:</b> The type of user or user level.
`,
    sortOrder: 5,
    detailsComponent: UsersLockedTotalDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: [...userAnalyticsTotalDataPointMap],
    excludeColumns: ["TotalUsers"],
    defaultLabels: [...userAnalyticsTotalDataPointMap],
  },
  UsersRemoved: {
    title: "Removed Users",
    uniqueId: "UsersRemoved",
    type: AnalyticsType.User,
    shortDescription: "Users removed within a period of time.",
    fullDescription: `
  Users removed by permission type within a period of time.<br>
  <b>Total:</b> Number of users removed during the time period.<br>
  <b>PermissionType:</b> The type of user or user level.
`,

    sortOrder: 6,
    detailsComponent: UsersRemovedDetailsComponent,
    chartTypeOptions: listOnlyOption,
    labelOptions: [...dataPointForRemoveActionsMap],
    excludeColumns: ["TotalUsers"],
    defaultLabels: [...dataPointForRemoveActionsMap],
  },
  UsersRemovedTotal: {
    title: "Removed Users Total",
    uniqueId: "UsersRemovedTotal",
    type: AnalyticsType.User,
    shortDescription:
      "Users removed by permission type within a period of time.",
    fullDescription: `
Users removed by permission type within a period of time.<br>
  <b>PermissionType:</b> The type of user or user level.<br>
  <b>User.FirstName:</b> The first name of the user.<br>
  <b>User.LastName:</b> The last name of the user.<br>
  <b>User.UserName:</b> The user's username.<br>
  <b>User.IsDeleted:</b> Whether or not the user has been deleted.<br>
  <b>RemovedBy:</b> Who removed the user account.<br>
  <b>RemovedDate:</b> When the user account was removed.
`,
    sortOrder: 7,
    detailsComponent: UsersRemovedTotalDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: [...userAnalyticsTotalDataPointMap],
    excludeColumns: ["TotalUsers"],
    defaultLabels: [...userAnalyticsTotalDataPointMap],
  },
  // AccountStatus: {
  //   title: "Account Status",
  //   type: AnalyticsType.User,
  //   description: "",
  //   sortOrder: 1,
  //   detailsComponent: AccountStatusDetailsComponent,
  // chartTypeOptions: barChartOptions,
  // },
  UsersSessionsActive: {
    title: "Active User Sessions",
    uniqueId: "UsersSessionsActive",
    type: AnalyticsType.User,
    shortDescription: "Users currently actively logged in by permission type.",
    fullDescription: `
  Active Sessions are users currently logged into the app.<br>
  <i><b>Note: </b>There is limited control when a session can be closed. If a user force closes the app, or never logs out, a session will not be closed until the next login. Use Expiration Time Period to control session expiration for reporting/analytics use. This filter will remove sessions without reported activity from the result set.</i><br>
  <b>Total:</b> A current snapshot of users currently logged in, allowing for control of how long an inactive session will be included in reported results.<br>
  <b>PermissionType:</b> The type of user or user level.
`,

    sortOrder: 1,
    detailsComponent: UsersSessionsActiveDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: [...dataPointForUserAnalyticsMap],
    excludeColumns: ["Id", "TotalUsers", "TotalSessions"],
    defaultLabels: [...dataPointForUserAnalyticsMap],
  },
  UserSessions: {
    title: "User Sessions",
    uniqueId: "UserSessions",
    type: AnalyticsType.User,
    shortDescription:
      "Total user sessions by permission type over a period of time.",
    fullDescription: `
  Active sessions during the time period, excluding deleted, locked, and archived users.<br>
  <b>RangeStart:</b> The start of the time range for this data point.<br>
  <b>RangeEnd:</b> The end of the time range for this data point.<br>
  <b>Total:</b> Number of sessions for the timeframe indicated.<br>
  <b>PermissionType:</b> The type of user or user level.
`,

    sortOrder: 1,
    detailsComponent: UsersSessionsDetailsComponent,
    chartTypeOptions: lineChartOptions,
    labelOptions: ["RangeStart", "RangeEnd", "PermissionType"],
    excludeColumns: [],
    defaultLabels: ["RangeStart", "RangeEnd", "PermissionType"],
  },
  // SessionsOld: {
  //   title: "User Sessions - Old",
  //   type: AnalyticsType.User,
  //   description: "",
  //   sortOrder: 1,
  //   detailsComponent: UsersSessionsComponent,
  // },
  TotalUsers: {
    title: "Total Users",
    uniqueId: "TotalUsers",
    type: AnalyticsType.User,
    shortDescription: "Total users by permission type over a period of time.",
    fullDescription: `
  Total users by permission type over a period of time.<br>
  <b>RangeStart:</b> The start of the range of time in which total users is calculated.<br>
  <b>RangeEnd:</b> The end of the range of time in which total users is calculated.<br>
  <b>Total:</b> Users include all active users, excluding deleted, locked, and archived users, before range end.<br>
  <b>Permission Groups:</b> The total users for each permission type for the given range start and range end.
`,

    sortOrder: 1,
    detailsComponent: UsersTotalDetailsComponent,
    chartTypeOptions: lineChartOptions,
    labelOptions: [...dataPointForTotalUserAnalyticsMap],
    excludeColumns: [],
    defaultLabels: [...dataPointForTotalUserAnalyticsMap],
    customLabels: {
      "Permission Groups": ["PermissionType", "Total"],
    },
  },
  AppLanguage: {
    title: "App Language",
    uniqueId: "AppLanguage",
    type: AnalyticsType.App,
    shortDescription: "Application Language set by users in app.",
    fullDescription: `
  Application language is set by users within the mobile apps via the settings. It is the UI language.<br>
  <b>Total:</b> A current snapshot of users’ settings based on their most recent login.<br>
  <i><b>Note:</b> Totals may not represent complete user counts and are limited to available data.</i><br>
  <b>Name:</b> The short and full name for the selected language.
`,

    sortOrder: 1,
    detailsComponent: AppLanguageDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: [...languageObjectMap],
    excludeColumns: [],
    defaultLabels: ["Language.Name"],
  },
  AppVersion: {
    title: "App Version",
    uniqueId: "AppVersion",
    type: AnalyticsType.App,
    shortDescription: "Operating system and version of app in use by session.",
    fullDescription: `
  The OS/version of apps in use. Data represents the most recent OS/app version collected with session data, within the time period set by the filter.<br>
  <b>Total:</b> A current snapshot of OS and app versions currently in use based on user sessions, within the selected time period.<br>
  <i><b>Note:</b> Totals for ‘All Time’ may not represent complete user counts and are limited to available data.</i><br>
  <b>App Type:</b> OS Type (Android, iOS, UWP, UWPSL)<br>
  <b>Version Number:</b> The major/minor version of ProManual<br>
  <b>Release Date:</b> The date the version was released.<br>
  <b>Required Update:</b> A version setting, determines if the user is required to install an update.<br>
  <b>Current Version:</b> This is the current/most up to date version released.<br>
  <b>Min Version:</b> When an update is required, optional setting to allow a range of versions. Usually not populated.
`,

    sortOrder: 3,
    detailsComponent: AppVersionDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: [...appVersionObjectMap],
    excludeColumns: [
      "PermissionId",
      "PermissionType",
      "TypeOfUser",
      "TotalUsers",
    ],
    defaultLabels: ["Version.AppType", "Version.VersionNumber"],
  },
  OsType: {
    title: "OS Type",
    uniqueId: "OsType",
    type: AnalyticsType.App,
    shortDescription: "Operating system type in use.",
    fullDescription: `
  The OS type of apps in use. Data represents the most recent OS type collected with session data, within the time period set by the filter.<br>
  <b>Total:</b> A current snapshot of OS types in use based on user sessions, within the selected time period.<br>
  <i><b>Note:</b> Totals for ‘All Time’ may not represent complete user counts and are limited to available data.</i><br>
  <b>App Type:</b> OS Type (Android, iOS, UWP, UWPSL). UWP is the Windows app downloaded via the Windows Store. UWPSL is the Windows app downloaded via the ProManual Admin web portal.
`,
    sortOrder: 4,
    detailsComponent: OsTypeDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: ["Version.AppType"],
    excludeColumns: [
      "TypeOfUser",
      "PermissionId",
      "PermissionType",
      "TotalUsers",
    ],
    defaultLabels: ["Version.AppType"],
  },
  OsVersion: {
    title: "OS Version",
    uniqueId: "OsVersion",
    type: AnalyticsType.App,
    shortDescription: "Operating system version in use.",
    fullDescription: `
  The OS version of apps in use. Data represents the most recent OS type collected with session data, within the time period set by the filter.<br>
  <b>Total:</b> A current snapshot of OS types in use based on user sessions, within the selected time period.<br>
  <i><b>Note:</b> Totals for ‘All Time’ may not represent complete user counts and are limited to available data.</i><br>
  <b>App Type:</b> OS Type (Android, iOS, UWP, UWPSL). UWP is the Windows app downloaded via the Windows Store. UWPSL is the Windows app downloaded via the ProManual Admin web portal.
  <b>Os Version:</b> The version of the OS the app is running on. For iOS, this is the major.minor version (e.g. 12.1). For Android, this is the API level (e.g. 28).
`,
    sortOrder: 4,
    detailsComponent: OsVersionsDetailsComponent,
    chartTypeOptions: barChartOptions,
    labelOptions: ["AppType", "OsVersion"],
    excludeColumns: [
      "TypeOfUser",
      "PermissionId",
      "PermissionType",
      "TotalUsers",
    ],
    defaultLabels: ["AppType", "OsVersion"],
  },
};
